<template>
  <div id="cycle-detail-table-container">
    <div class="cdt-container">
      <v-data-table
        :headers="table"
        :items="cycleTableData"
        :loading="cycleLoading"
        :options="cdTableOptions"
        multi-sort
        hide-default-footer
        fixed-header
        height="700px"
      >
        <template #[`item.cycleStatus`]="{ item }">
          <td>
            <div class="cycle-status">
              <div :class="[setAlertDot(item), 'dot', 'align-status']" />
              <span :class="[`${setAlertDot(item)}-text`]">{{ getAlertSeverity(item) }}</span>
            </div>
          </td>
        </template>

        <template #[`item.OATemp`]="{ item }">
          <td>
            {{ getOATemp(item, tempFormat) }}
          </td>
        </template>

        <template #[`item.deltaP`]="{ item }">
          <td>
            {{ convertDeltaPFormat(item) }}
          </td>
        </template>

        <template #[`item.performance`]="{ item }">
          <td>
            <span
              :class="[`${determineACMode(item)}-text`]"
            >
              {{ determineDeltaTFormat(item) }}
            </span>
          </td>
        </template>

        <template #[`item.sup`]="{ item }">
          <td>
            {{ supRetFormat(item) }}
          </td>
        </template>

        <template #[`item.atticAir.avgTemp`]="{ item }">
          <td>
            {{ calculateTempUnit(item.atticAir?.avgTemp) }}
          </td>
        </template>

        <template #[`item.startTime`]="{ item }">
          <td>
            {{ timeInHours(item.startTime) }}
          </td>
        </template>

        <template #[`item.cycleTimeSeconds`]="{ item }">
          <td>
            {{ runtime(item.cycleTimeSeconds) }}
          </td>
        </template>

        <template #[`item.valid`]="{ item }">
          <td>
            <div :class="[setIsValidDot(item.valid)]" />
          </td>
        </template>

        <template #[`item.supplyAir.humidity`]="{ item }">
          <td>
            <div>{{ setHumidity(item.supplyAir.humidity) }}</div>
          </td>
        </template>

        <template #[`item.rooms.temps`]="{ item }">
          <td>
            <div>{{ setRoomTemp(item.rooms) }}</div>
          </td>
        </template>

        <template #[`item.rooms.humidity`]="{ item }">
          <td>
            <div>{{ setRoomHumidity(item.rooms) }}</div>
          </td>
        </template>

        <template #[`item.startDate`]="{ item }">
          <td>
            <div>{{ dateWithoutHour(item.startDate, dateAndYearFormat) }}</div>
          </td>
        </template>
        <template #[`item.cycleChart`]="{ item }">
          <td>
            <div class="export-csv-button-container">
              <button
                class="export-csv-cycle-button"
                @click="showCycleChart(item)"
              >
                <span class="icon-luc-ArrowRight sac__font-14 n90"></span>
              </button>
            </div>
          </td>
        </template>
      </v-data-table>
      <ProcessOverlay
        :show="generatingCSV"
        text="Generating CSV"
        overlayClass="ac-overlay"
      />
    </div>
    <SACTableFooter
      :totalRecords="cycleTableData.length"
      :rowCount="cdTableOptions.itemsPerPage"
      :page="cdTableOptions.page"
      :changeRowCount="changeRowCount"
      :changePage="changePage"
      :totalPage="cdTableRecordCount"
      :loading="cycleLoading"
    />
  </div>
</template>

<script>
import moment from 'moment';

import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SACTableFooter from '@shared/components/table/SACTableFooter.vue';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

export default {
  name: 'CycleTable',
  components: {
    SACTableFooter,
    ProcessOverlay,
  },
  mixins: [customerDetailMixin, unitPreferencesMixin],
  computed: {
    table() {
      const headers = [{
        text: 'AC Mode',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'acMode',
      }, {
        text: 'Thermostat Zone',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'tZone',
      }, {
        text: 'OA Temp/RH',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'OATemp',
      }, {
        text: 'Delta P',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'deltaP',
      }, {
        text: 'Performance',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'performance',
      }, {
        text: 'Sup/Ret',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'sup',
      }];
      if (this.sensorCheck('A2TempHumidity')) {
        headers.push({
          text: 'SUP RH',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'supplyAir.humidity',
        });
      }
      if (this.sensorCheck('A2RoomTempHumidity')) {
        headers.push({
          text: 'Room Temp',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'rooms.temps',
        }, {
          text: 'Room RH',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'rooms.humidity',
        });
      }
      headers.push({
        text: 'Leak',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'atticAir.avgTemp',
      }, {
        text: 'Time',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'startTime',
      }, {
        text: 'Runtime',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'cycleTimeSeconds',
      }, {
        text: 'Date',
        align: 'start',
        sortable: true,
        filterable: false,
        value: 'startDate',
      }, {
        text: '',
        align: 'end',
        sortable: false,
        filterable: false,
        value: 'valid',
      });
      if (this.isSmacAdmin) {
        headers.push({
          text: '',
          align: 'end',
          sortable: false,
          filterable: false,
          value: 'cycleChart',
        });
      }
      return headers;
    },
  },
  methods: {
    showCycleChart(data) {
      const {
        uid,
        startTime,
        endTime,
      } = data;
      const newStartTime = moment(startTime).subtract('15', 'minutes').toISOString();
      const newEndTime = moment(endTime).add('15', 'minutes').toISOString();

      const route = this.$router.resolve({
        path: `/device/${uid}`,
        query: {
          activeTab: 'chart',
          startTime: newStartTime,
          endTime: newEndTime,
        },
      });
      window.open(route.href, '_blank');
    },
    changePage(page) {
      this.$store.dispatch('changeCdTableOption', {
        field: 'page',
        value: page,
      });
    },
    changeRowCount(rowCount) {
      this.$store.dispatch('changeCdTableOption', {
        field: 'itemsPerPage',
        value: rowCount,
      });
    },
    setHumidity(humidity) {
      if (humidity && humidity.avg) {
        return `${humidity.avg.toFixed(0)}`;
      }
      return '--';
    },
    setRoomTemp(temps) {
      if (temps) {
        let firstRoom = null;
        if (Object.keys(temps).length > 0) {
          firstRoom = temps[Object.keys(temps)[0]];
        }
        if (firstRoom) {
          const temp = firstRoom.temps.end;
          if (temp) {
            return `${temp.toFixed(0)}`;
          }
        }
      }
      return '--';
    },
    setRoomHumidity(humidity) {
      if (humidity) {
        let firstRoom = null;
        if (Object.keys(humidity).length > 0) {
          firstRoom = humidity[Object.keys(humidity)[0]];
        }
        if (firstRoom) {
          return firstRoom.humidity.end;
        }
      }
      return '--';
    },
    sensorCheck(type) {
      return this.sensors.find((s) => s.sensorType === type);
    },
  },
};
</script>

<style lang="scss">

#cycle-detail-table-container{
  width: 100%;
  max-width: 1705px;
  .cdt-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;
    .v-data-table__wrapper {
      height: 700px;
      thead {
        tr {
          th {
            text-transform: uppercase;
            background-color: $N100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;
            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }
          }
        }
      }
      tbody {
        tr {
          td {
            .cycle-status {
              display: flex;
              justify-content: center;
              align-items: center;
            }
            border-bottom: 0;
            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }
            &:first-child {
              max-width: 110px;
              justify-content: flex-start;
            }
            &:last-child {
              justify-content: center;
              max-width: 100px;
            }
            .yellow-text {
              color: $SUNSET100;
            }
            .red-text {
              color: $TOMATO100;
              font-weight: 500;
            }
            .blue-text {
              color: $NBLUE90;
              font-weight: 500;
            }
            .dot {
              width: 8px;
              height: 8px;
              border-radius: 4px;
              margin-right: 8px;
              &.green {
                background-color: $BASIL100;
              }
              &.yellow {
                background-color: $SUNSET100;
              }
              &.red {
                background-color: $TOMATO100;
              }
            }
          }
          &:hover {
            background: none;
          }
          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }
  }
  .export-csv-button-container {
    margin-right: 6px;
    display: flex;
    align-items: center;

    .export-csv-cycle-button {
      min-width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $WHITE;
      font-size: 12.64px;
      &:disabled {
        background-color: $N50
      };
      .loading {
        width: 20px;
      }
    }
    .export-csv-icon {
      min-width: 18px;
      height: 18px;
    }
  }
  .info-container {
    position: relative;
    cursor: pointer;
    .info-tooltip {
      display: none;
      position: absolute;
      width: 500px;
      left: -500px;
      background-color: $WHITE;
      padding: 12px;
      border-radius: 4px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
      &.bottom {
        bottom: 0;
      }
      &.top {
        top: 0;
      }
    }
    &:hover {
      .info-tooltip {
        display: flex;
      }
    }
  }
}
</style>
